// Fragment shaders, one per pass family. Per-pixel colour passes are GLSL functions that the
// colour-shader builder (colorShader.ts) chains; neighbourhood passes are full programs.
import { COLOR_FNS, HEADER, OKLAB_FNS } from './common';
import { GRAIN_FNS } from './effectsPasses';

/**
 * Area downsample of the source into a linear RGBA16F texture (also linearizes 8-bit encoded input).
 * Averages premultiplied colour over the output pixel's footprint with up to 8×8 bilinear taps.
 * uRect: the source sub-rectangle (normalized) mapped onto the output.
 */
export const DOWNSAMPLE = `${HEADER}${COLOR_FNS}
uniform sampler2D uSrc;
uniform vec2 uSrcSize;
uniform vec4 uRect;      // x, y, w, h in source-normalized
uniform vec2 uOutSize;
uniform int uEncoded;    // 1 = 8-bit P3-encoded input, 0 = linear
uniform int uInPremul;   // 1 = input is already premultiplied
uniform int uOutPremul;  // 1 = write premultiplied (the preview texture), 0 = straight
out vec4 o;
vec4 texelLin(ivec2 t) {
  vec4 c = texelFetch(uSrc, clamp(t, ivec2(0), ivec2(uSrcSize) - 1), 0);
  if (uEncoded == 1) c.rgb = srgbToLin3(c.rgb);
  if (uInPremul == 0) c.rgb *= c.a;
  return c;
}
vec4 fetchLin(vec2 uv) {
  if (uInPremul == 1 && uEncoded == 0) return texture(uSrc, uv);
  // Straight-alpha or encoded input: linearize and premultiply each texel BEFORE the bilinear mix, or the
  // colour under alpha 0 bleeds into the edge (CODE_REVIEW S6).
  vec2 px = uv * uSrcSize - 0.5;
  ivec2 i0 = ivec2(floor(px));
  vec2 f = px - floor(px);
  return mix(
    mix(texelLin(i0), texelLin(i0 + ivec2(1, 0)), f.x),
    mix(texelLin(i0 + ivec2(0, 1)), texelLin(i0 + ivec2(1, 1)), f.x),
    f.y
  );
}
void main() {
  vec2 footprint = uRect.zw * uSrcSize / uOutSize;           // source pixels per output pixel
  int nx = int(clamp(ceil(footprint.x * 0.5), 1.0, 8.0));
  int ny = int(clamp(ceil(footprint.y * 0.5), 1.0, 8.0));
  vec2 base = uRect.xy + (gl_FragCoord.xy - 0.5) / uOutSize * uRect.zw;
  vec2 stepUv = (uRect.zw / uOutSize) / vec2(float(nx), float(ny));
  vec4 acc = vec4(0.0);
  for (int j = 0; j < 8; j++) {
    if (j >= ny) break;
    for (int i = 0; i < 8; i++) {
      if (i >= nx) break;
      acc += fetchLin(base + (vec2(float(i), float(j)) + 0.5) * stepUv);
    }
  }
  acc /= float(nx * ny);
  o = uOutPremul == 1 ? acc : vec4(acc.a > 0.0 ? acc.rgb / acc.a : vec3(0.0), acc.a);
}`;

/** Guide prep: WB + exposure on the low-res linear image, output luminance (R channel). */
export const GUIDE_LUMA = `${HEADER}${COLOR_FNS}
uniform sampler2D uSrc;
uniform vec2 uOutSize;
uniform mat3 uWb;
uniform float uGain;
out vec4 o;
void main() {
  vec2 uv = gl_FragCoord.xy / uOutSize;
  vec3 c = uWb * texture(uSrc, uv).rgb * uGain;
  o = vec4(max(lumaP3(c), 1e-6), 0.0, 0.0, 1.0);
}`;

/** Separable Gaussian blur of the R channel (uDir = (1,0) or (0,1)), clamp-to-edge. */
export const BLUR_R = `${HEADER}
uniform sampler2D uSrc;
uniform vec2 uOutSize;
uniform vec2 uDir;
uniform float uSigma;
out vec4 o;
void main() {
  vec2 px = 1.0 / uOutSize;
  int r = int(min(ceil(uSigma * 3.0), 32.0));
  float acc = 0.0, wsum = 0.0;
  for (int k = -32; k <= 32; k++) {
    if (k < -r || k > r) continue;
    float w = exp(-0.5 * float(k * k) / (uSigma * uSigma));
    vec2 uv = clamp((gl_FragCoord.xy + uDir * float(k)) * px, 0.5 * px, 1.0 - 0.5 * px);
    acc += w * texture(uSrc, uv).r;
    wsum += w;
  }
  o = vec4(acc / wsum, 0.0, 0.0, 1.0);
}`;

/**
 * Warp (§6.2 pass 17): samples the colour-processed region texture through the geometry matrix.
 * Input is PREMULTIPLIED; bilinear (preview) or Catmull-Rom (export) filtering happens on premultiplied
 * values, and the result is unpremultiplied (§6.2 alpha rule). Outside the source → alpha 0.
 */
export const WARP = `${HEADER}
uniform sampler2D uSrc;      // premultiplied linear region texture
uniform vec2 uOutSize;
uniform vec2 uOutOffset;     // tile origin in full output pixels
uniform mat3 uOutToSrc;      // output pixel → source normalized (homogeneous)
uniform vec4 uRegion;        // region rect in source normalized (x, y, w, h)
uniform vec2 uRegionSize;    // region texture size in pixels
uniform int uCubic;
out vec4 o;
vec4 cubicW(float t) {
  // Catmull-Rom weights
  float t2 = t * t, t3 = t2 * t;
  return vec4(-0.5 * t3 + t2 - 0.5 * t, 1.5 * t3 - 2.5 * t2 + 1.0, -1.5 * t3 + 2.0 * t2 + 0.5 * t, 0.5 * t3 - 0.5 * t2);
}
vec4 sampleCubic(vec2 uv) {
  vec2 p = uv * uRegionSize - 0.5;
  vec2 f = fract(p);
  vec2 i0 = floor(p);
  vec4 wx = cubicW(f.x), wy = cubicW(f.y);
  vec4 acc = vec4(0.0);
  for (int j = 0; j < 4; j++) {
    for (int i = 0; i < 4; i++) {
      vec2 q = (i0 + vec2(float(i) - 1.0, float(j) - 1.0) + 0.5) / uRegionSize;
      acc += texture(uSrc, clamp(q, 0.5 / uRegionSize, 1.0 - 0.5 / uRegionSize)) * wx[i] * wy[j];
    }
  }
  return acc;
}
void main() {
  vec3 s = uOutToSrc * vec3(gl_FragCoord.xy + uOutOffset, 1.0);
  vec2 src = s.xy / s.z;
  // Half-pixel tolerance at the edge of the source, else alpha 0.
  if (src.x < 0.0 || src.y < 0.0 || src.x > 1.0 || src.y > 1.0) { o = vec4(0.0); return; }
  vec2 uv = (src - uRegion.xy) / uRegion.zw;
  vec4 c = uCubic == 1 ? sampleCubic(uv) : texture(uSrc, uv);
  c.a = clamp(c.a, 0.0, 1.0);
  o = vec4(c.a > 1e-6 ? c.rgb / c.a : vec3(0.0), c.a);
}`;

/**
 * Output transform (§6.2 pass 18): working linear P3 → output space, transfer curve, fill composite
 * when the output has no alpha, optional clipping overlay. Writes encoded values (straight alpha).
 */
export const OUTPUT = `${HEADER}${COLOR_FNS}${OKLAB_FNS}${GRAIN_FNS}
uniform sampler2D uSrc;
uniform vec2 uOutSize;
uniform mat3 uToOut;         // linear P3 → linear output space
uniform int uAdobe;          // 1 = Adobe RGB gamma 563/256, else sRGB curve
uniform int uFlatten;        // 1 = composite onto uFill (no alpha in output)
uniform vec3 uFill;          // encoded fill colour in the output space
uniform int uClipping;       // 1 = show clipping overlay
uniform int uFlipY;          // 1 when drawing to the default framebuffer
uniform vec2 uOrigin;        // viewport origin in framebuffer pixels
uniform int uView;           // canvas only: 0 none, 1 checker, 2 white, 3 black, 4 mask overlay, 5 matte
out vec4 o;
float enc(float l) { return uAdobe == 1 ? pow(max(l, 0.0), 1.0 / 2.19921875) : linToSrgb(max(l, 0.0)); }
void main() {
  vec2 fc = gl_FragCoord.xy - uOrigin;
  if (uFlipY == 1) fc.y = uOutSize.y - fc.y;
  vec4 c = texture(uSrc, fc / uOutSize);
  vec3 lin = clamp(gamutOut(c.rgb, uToOut), 0.0, 1.0);
  vec3 e = vec3(enc(lin.r), enc(lin.g), enc(lin.b));
  float a = clamp(c.a, 0.0, 1.0);
  e = addGrain(e, fc);
  if (uClipping == 1) {
    if (max(e.r, max(e.g, e.b)) >= 0.998) e = vec3(1.0, 0.0, 0.0);
    else if (max(e.r, max(e.g, e.b)) <= 0.002) e = vec3(0.0, 0.25, 1.0);
  }
  if (uFlatten == 1) { e = mix(uFill, e, a); a = 1.0; }
  if (uView == 1) {
    ivec2 q = ivec2(gl_FragCoord.xy) / 8;
    e = mix(vec3((q.x + q.y) % 2 == 0 ? 0.8 : 0.6), e, a);
    a = 1.0;
  } else if (uView == 2) { e = mix(vec3(1.0), e, a); a = 1.0; }
  else if (uView == 3) { e = mix(vec3(0.0), e, a); a = 1.0; }
  else if (uView == 4) { e = mix(e, vec3(1.0, 0.0, 0.0), 0.5 * (1.0 - a)); a = 1.0; }
  else if (uView == 5) { e = vec3(a); a = 1.0; }
  o = vec4(e, a);
}`;
